import { useState } from 'react';
import { Button } from '~/components/ui/button';
import { Plus } from 'lucide-react';
import { Badge, type BadgeProps } from '~/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { ReceiptDialog } from './ReceiptDialog';
import { GuardianInviteStatus, type GuardianInviteRow } from './GuardianInviteStatus';
import { useResendEnrollmentInvite } from './useResendEnrollmentInvite';
import { inviteResultLabel } from '../Enrollments/inviteWords';
import { completedViaLabel, deadlineLabel, formatChecklistDate, itemStateLabel, progressLabel } from './checklistWords';
import { ChecklistItemMenu, type ChecklistMenuItem } from './ChecklistItemMenu';
import type { TuitionPlan } from './SetTuitionDialog';
import { AddTodoDialog } from './AddTodoDialog';
import { MarkEnrolledDialog } from './MarkEnrolledDialog';

export interface ChecklistCardItem {
  id: string;
  title: string;
  /** 1 for the first agreement and fee; later ones are changes the office made, and the server titles them. */
  round?: number;
  personName?: string | null;
  state: string;
  /** A later round's agreement the office kept as it is: it reads Kept as it is, not Waived. */
  keptAsItIs?: boolean;
  completedAt?: string | null;
  completedVia?: string | null;
  stepType?: string | null;
  required?: boolean;
  officeOnly?: boolean;
  /** Why a locked item can't be started yet, in the office's words for the office. Only the family page selects this. */
  lockedReason?: string | null;
  /** Admin-only: on the agreement step. */
  tuitionAgreement?: ChecklistMenuItem['tuitionAgreement'];
  /** Admin-only: on the fee step, once it has an invoice. */
  feeInvoiceId?: string | null;
}

export interface ChecklistCardData {
  id: string;
  completedAt?: string | null;
  deadlineOn?: string | null;
  late?: boolean;
  progress: { done: number; total: number; waitingOn?: string[] };
  items: ChecklistCardItem[];
  /** Only the family page selects this; it is not batched, so list rows leave it off. */
  guardians?: GuardianInviteRow[] | null;
}

export interface ChecklistCardProps {
  checklist: ChecklistCardData;
  familyPath: string;
  familyEnrollmentId: string;
  schoolId: string;
  /** The school's checklist-enrollment switch: with it off the card shows nothing about invitations. */
  showInvite?: boolean;
  /** A year the office invited on the new flow, enrolling or enrolled, that a reminder can go to. */
  canResend?: boolean;
  /** The school's switch: the office can mark items done, waive or reopen them, and add to-dos. */
  showOfficeActions?: boolean;
  /** A new-flow year that is enrolling, with the switch on. */
  canMarkEnrolled?: boolean;
  familyName?: string;
  yearName?: string;
  /** The children of the year, for a to-do that is for one of them. */
  childrenOfFamily?: { id: string; name: string }[];
  /** The names of the children Coming, who become Enrolled with the family. */
  comingChildren?: string[];
  /** The year's plans, every one, for Set tuition. */
  plans?: TuitionPlan[];
  /** The family year's invoices, to find the fee's amount for Record a check. */
  invoices?: { id: string; amountCents: number }[];
  onChanged?: () => void;
}

const stateVariants: Record<string, BadgeProps['variant']> = {
  DONE: 'default',
  IN_PROGRESS: 'secondary',
  OPEN: 'outline',
  LOCKED: 'outline',
  WAIVED: 'secondary',
  NOT_NEEDED: 'secondary',
};

export function ChecklistCard({ checklist, familyPath, familyEnrollmentId, schoolId, showInvite = false,
  canResend = false,
  showOfficeActions = false,
  canMarkEnrolled = false,
  familyName = '',
  yearName = '',
  childrenOfFamily = [],
  comingChildren = [],
  plans = [],
  invoices = [],
  onChanged,
}: ChecklistCardProps) {
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const [addingTodo, setAddingTodo] = useState(false);
  const [markingEnrolled, setMarkingEnrolled] = useState(false);
  const [resendNote, setResendNote] = useState<{ text: string; failed: boolean } | null>(null);
  const [resendInvite, { loading: resending }] = useResendEnrollmentInvite();
  const open = !checklist.completedAt;
  const guardians = checklist.guardians ?? [];
  const deadline = deadlineLabel(checklist);
  // The office's own dialog, so its own to-dos count: they stay open too.
  // Who's coming is left out: it reads Not needed once the year is Enrolled.
  const openSteps = checklist.items.filter(
    (item) => item.required && item.stepType !== 'who' && ['OPEN', 'IN_PROGRESS', 'LOCKED'].includes(item.state)
  ).length;
  // As the server refuses: Who's coming is settled once the year is enrolled (the checklist finished) or the agreement signed.
  const whoClosed = !open || checklist.items.some((item) => item.stepType === 'agreement' && item.tuitionAgreement?.state === 'SIGNED');
  // As the server sends it: on an enrolled year (the checklist finished) only while a required step is still the family's
  // to do, say a sibling added since; an office-only to-do never reaches them.
  const familyStepOpen = checklist.items.some(
    (item) => item.required && !item.officeOnly && ['OPEN', 'IN_PROGRESS', 'LOCKED'].includes(item.state)
  );
  const canRemind = canResend && (open || familyStepOpen);

  const handleResend = async () => {
    setResendNote(null);
    try {
      const result = await resendInvite({ variables: { input: { familyEnrollmentId } } });
      const payload = result.data?.resendEnrollmentInvite;
      if (payload?.errors && payload.errors.length > 0) {
        setResendNote({ text: payload.errors.join(', '), failed: true });
        return;
      }
      const outcome = payload?.result;
      if (!outcome || outcome.status === 'REFUSED') {
        setResendNote({ text: `${inviteResultLabel('REFUSED')}${outcome?.reason ? ` — ${outcome.reason}` : ''}`, failed: true });
        return;
      }
      setResendNote({ text: inviteResultLabel(outcome.status), failed: false });
      onChanged?.();
    } catch (error) {
      setResendNote({ text: error instanceof Error ? error.message : 'The reminder was not sent. Please try again.', failed: true });
    }
  };

  const groups: { name: string; items: ChecklistCardItem[] }[] = [];
  const family = checklist.items.filter((item) => !item.personName);
  if (family.length) groups.push({ name: 'Family', items: family });
  for (const item of checklist.items) {
    if (!item.personName) continue;
    let group = groups.find((g) => g.name === item.personName);
    if (!group) {
      group = { name: item.personName, items: [] };
      groups.push(group);
    }
    group.items.push(item);
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Checklist</CardTitle>
        <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-sm text-muted-foreground">
          {deadline && <span className={checklist.late ? 'text-destructive' : undefined}>{deadline}</span>}
          <span>{progressLabel(checklist)}</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showInvite && ((open && guardians.length > 0) || canRemind) && (
          <div className="space-y-2">
            {open && <GuardianInviteStatus guardians={guardians} />}
            {canRemind && (
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="outline" size="sm" onClick={handleResend} disabled={resending}>
                  {resending ? 'Sending…' : 'Send a reminder'}
                </Button>
                {resendNote && (
                  <span role={resendNote.failed ? 'alert' : 'status'} className={resendNote.failed ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>
                    {resendNote.text}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
        {showOfficeActions && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setAddingTodo(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add a to-do
            </Button>
            {canMarkEnrolled && (
              <Button variant="outline" size="sm" onClick={() => setMarkingEnrolled(true)}>
                Mark enrolled
              </Button>
            )}
          </div>
        )}
        {groups.map((group) => (
          <div key={group.name} className="space-y-2">
            <h4 className="font-semibold text-sm">{group.name}</h4>
            {/*
              * A row per item rather than a table: on a phone the title, state
              * and date stack, and View stays at the row's right edge instead of
              * scrolling off the card.
              */}
            <ul className="divide-y rounded-md border">
              {group.items.map((item) => (
                <li key={item.id} className="flex items-center gap-3 px-3 py-2">
                  <div className="min-w-0 flex-1 space-y-1 sm:flex sm:items-center sm:gap-4 sm:space-y-0">
                    <div className="min-w-0 sm:flex-1">
                      <p className="break-words text-sm">{item.title}</p>
                      {item.state === 'LOCKED' && item.lockedReason && (
                        <p className="break-words text-xs text-muted-foreground">{item.lockedReason}</p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:shrink-0">
                      <Badge variant={stateVariants[item.state] ?? 'secondary'}>{itemStateLabel(item.state, item.keptAsItIs)}</Badge>
                      {item.officeOnly && <Badge variant="outline">Office only</Badge>}
                      {item.completedAt && (
                        <span className="text-sm text-muted-foreground">
                          {`${formatChecklistDate(item.completedAt)} · ${completedViaLabel(item.completedVia)}`}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    aria-label={`View ${item.title}`}
                    onClick={() => setOpenItemId(item.id)}
                  >
                    View
                  </Button>
                  {showOfficeActions && (
                    <ChecklistItemMenu item={item} familyEnrollmentId={familyEnrollmentId} plans={plans} invoices={invoices}
                      whoClosed={whoClosed} onChanged={onChanged} />
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </CardContent>
      {showOfficeActions && (
        <AddTodoDialog
          open={addingTodo}
          onOpenChange={setAddingTodo}
          familyEnrollmentId={familyEnrollmentId}
          childOptions={childrenOfFamily}
          onSuccess={onChanged}
        />
      )}
      {canMarkEnrolled && (
        <MarkEnrolledDialog
          open={markingEnrolled}
          onOpenChange={setMarkingEnrolled}
          familyEnrollmentId={familyEnrollmentId}
          familyName={familyName}
          yearName={yearName}
          openSteps={openSteps}
          comingChildren={comingChildren}
          onSuccess={onChanged}
        />
      )}
      <ReceiptDialog
        itemId={openItemId}
        schoolId={schoolId}
        familyPath={familyPath}
        familyEnrollmentId={familyEnrollmentId}
        open={openItemId !== null}
        onOpenChange={(open) => { if (!open) setOpenItemId(null); }}
      />
    </Card>
  );
}

export default ChecklistCard;
